import type {
  ActualParty,
  ActualPlayerBank,
  PvpRouletteResolvedRound,
  RoundBet,
  RoundDetailUserData,
  RoundTrick,
} from "@/shared/api/endpoints"

/**
 * Публичная страница проверки честности (GitHub Pages), без привязки к API приложения.
 * Хэш `#d=` = base64url(gzip(JSON)) — совпадает с `decodePayloadFromLocation`
 * в репозитории legit-check (`scripts/url-payload.js`).
 */
export const LEGIT_CHECK_PAGE_BASE_URL =
  "https://trix-public.github.io/legit-check"

/** Из user_data выкидываем photo_url — он длинный и не нужен для верификации/UI. */
function slimUser(u: RoundDetailUserData): RoundDetailUserData {
  return { user_id: u.user_id, username: u.username ?? null }
}

/** Поля ставки, которые реально используются на странице (агрегация + время). */
function slimBet(b: RoundBet) {
  return {
    id: b.id,
    user_data: slimUser(b.user_data),
    party_id: b.party_id,
    party_type: b.party_type,
    asset_class: b.asset_class,
    amount: b.amount,
    created_at: b.created_at,
  }
}

function slimPlayerBank(pb: ActualPlayerBank) {
  return { user_data: slimUser(pb.user_data), bank: pb.bank }
}

function slimParty(p: ActualParty) {
  return {
    id: p.id,
    type: p.type,
    total_bank: p.total_bank,
    win_probability: p.win_probability,
    sector_from: p.sector_from,
    sector_to: p.sector_to,
    player_banks: p.player_banks.map(slimPlayerBank),
  }
}

function slimTrick(t: RoundTrick) {
  return { user_id: t.user_id, type: t.type }
}

/**
 * Минимизированная копия раунда: только поля, нужные для верификации
 * и отрисовки на странице legit-check. `fairness_preimage` НЕ трогаем —
 * любой байт внутри ломает хэш.
 */
export function buildLegitCheckRoundPayload(round: PvpRouletteResolvedRound) {
  return {
    id: round.id,
    number: round.number,
    room_name: round.room_name,
    fairness_preimage: round.fairness_preimage ?? null,
    salt: round.salt,
    commit_hash: round.commit_hash,
    client_commit_hash: round.client_commit_hash,
    roulette_position: round.roulette_position,
    created_at: round.created_at,
    parties: round.parties.map(slimParty),
    bets: round.bets.map(slimBet),
    tricks: round.tricks.map(slimTrick),
  }
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = ""
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!)
  return btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "")
}

async function gzipUtf8(text: string): Promise<Uint8Array> {
  const utf8 = new TextEncoder().encode(text)
  const stream = new Blob([utf8])
    .stream()
    .pipeThrough(new CompressionStream("gzip"))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/**
 * Базовый URL страницы проверки + #d=<gzip+base64url>. Хэш-фрагмент не уходит
 * на сервер, поэтому URI Too Long (414) исключён. Если в браузере нет
 * CompressionStream, падаем на несжатый `#p=`.
 */
export async function buildLegitCheckExternalUrl(
  round: PvpRouletteResolvedRound,
): Promise<string> {
  const payload = buildLegitCheckRoundPayload(round)
  const json = JSON.stringify(payload)
  const base = LEGIT_CHECK_PAGE_BASE_URL.replace(/\/$/, "")
  if (typeof CompressionStream === "function") {
    try {
      const gz = await gzipUtf8(json)
      return `${base}/#d=${bytesToBase64Url(gz)}`
    } catch {
      // fall through to uncompressed
    }
  }
  const utf8 = new TextEncoder().encode(json)
  return `${base}/#p=${bytesToBase64Url(utf8)}`
}
